/**
 * FormativeCheckBlock — self-assessed "fix these prompts" formative check.
 *
 * Replaces the old inline answer-key dump (raw <details> HTML that rendered as
 * literal text with every answer visible at once). Presents ONE broken prompt
 * at a time: the student classifies the failure mode against the Segment-1
 * taxonomy and sketches a fix, then reveals the canonical answer to self-check,
 * then advances. No LLM grading and no auth — it's a reflection exercise, so
 * the answer ships with the data and the reveal is purely client-side.
 *
 * Renders nothing when the module has no formative check (or is locked).
 */

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getModuleFormative, ModuleFormative } from "../api/client";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export default function FormativeCheckBlock({ slug }: { slug: string }) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const { data } = useQuery<ModuleFormative | null>({
    queryKey: ["formative", slug],
    queryFn: () => getModuleFormative(slug),
    retry: false,
  });

  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState("");
  const [fix, setFix] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [finished, setFinished] = useState(false);

  if (!data || data.items.length === 0) return null;

  const total = data.items.length;
  const item = data.items[index];
  const isLast = index === total - 1;
  const correctMode = revealed && mode === item.answer;
  const itemLabel = data.item_label || t("FormativeCheckBlock.item");
  const choiceLabel = data.choice_label || t("FormativeCheckBlock.yourAnswer");
  const revealLabel = data.reveal_label || t("FormativeCheckBlock.answer");

  const advance = () => {
    if (isLast) {
      setFinished(true);
      return;
    }
    setIndex((i) => i + 1);
    setMode("");
    setFix("");
    setRevealed(false);
  };

  const restart = () => {
    setIndex(0);
    setMode("");
    setFix("");
    setRevealed(false);
    setFinished(false);
  };

  const Header = (
    <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" gap={0.5}>
      <FactCheckIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.primary.main }}
      >
        {t("FormativeCheckBlock.heading")}
      </Typography>
      {data.title && (
        <Typography variant="caption" color="text.secondary">
          · {data.title}
        </Typography>
      )}
    </Stack>
  );

  if (finished) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          my: 3,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        {Header}
        <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
          {t("FormativeCheckBlock.finishedAll", { total })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("FormativeCheckBlock.finishedHint")}
        </Typography>
        <Button
          variant="outlined"
          onClick={restart}
          sx={{ textTransform: "none", borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
        >
          {t("FormativeCheckBlock.startOver")}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        my: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}
    >
      {Header}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5 }}
      >
        {t("FormativeCheckBlock.selfCheckHint")}
      </Typography>

      {index === 0 && data.instructions && (
        <Typography variant="body2" color="text.primary" sx={{ mb: 2, lineHeight: 1.7 }}>
          {data.instructions}
        </Typography>
      )}

      {/* Progress */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" fontWeight={500} color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {t("FormativeCheckBlock.itemProgress", { current: index + 1, total })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(index / total) * 100}
          sx={{
            flex: 1,
            height: 6,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.main },
          }}
        />
      </Stack>

      {/* Item prompt */}
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {itemLabel}
      </Typography>
      <Box
        sx={{
          mt: 0.75,
          mb: 1,
          p: 1.5,
          borderRadius: 2,
          fontFamily: MONO,
          fontSize: "0.85rem",
          bgcolor: alpha(theme.palette.text.primary, 0.04),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
          whiteSpace: "pre-wrap",
        }}
      >
        {item.prompt}
      </Box>
      {item.context && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {item.context}
        </Typography>
      )}

      {/* Diagnosis input */}
      <FormControl size="small" fullWidth sx={{ mb: 2, maxWidth: 420 }} disabled={revealed}>
        <InputLabel id={`fm-${item.id}`}>{choiceLabel}</InputLabel>
        <Select
          labelId={`fm-${item.id}`}
          label={choiceLabel}
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          {data.options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        multiline
        minRows={2}
        fullWidth
        size="small"
        placeholder={t("FormativeCheckBlock.reasoningPlaceholder")}
        value={fix}
        onChange={(e) => setFix(e.target.value)}
        disabled={revealed}
        sx={{ mb: 2 }}
      />

      {/* Reveal / answer */}
      {!revealed ? (
        <Button
          variant="contained"
          disabled={!mode}
          onClick={() => setRevealed(true)}
          sx={{
            bgcolor: theme.palette.primary.main,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            "&.Mui-disabled": { bgcolor: alpha(theme.palette.primary.main, 0.35), color: "white" },
          }}
        >
          {t("FormativeCheckBlock.revealAnswer")}
        </Button>
      ) : (
        <>
          <Alert
            icon={correctMode ? <CheckCircleIcon /> : <CancelIcon />}
            severity={correctMode ? "success" : "warning"}
            sx={{ borderRadius: 2, mb: 1.5 }}
          >
            {correctMode
              ? t("FormativeCheckBlock.correctAnswer")
              : t("FormativeCheckBlock.incorrectAnswer", { mode })}
          </Alert>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              {choiceLabel}:
            </Typography>
            <Chip
              label={item.answer}
              size="small"
              sx={{ fontWeight: 500, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
            />
          </Stack>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>{revealLabel}:</strong> {item.explanation}
          </Typography>

          <Button
            variant="contained"
            onClick={advance}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {isLast
              ? t("FormativeCheckBlock.finish")
              : t("FormativeCheckBlock.nextPrompt")}
          </Button>
        </>
      )}
    </Paper>
  );
}
